/**
 * Practice Settings — admin page that persists the QIP / Medicare / HealthLink
 * identifiers Nash needs once and reuses everywhere. Single record stored in
 * `db.practice_settings`. Read by anyone authenticated; write is admin-gated.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Save, Sparkles, Building2, Receipt, Shield, MapPin, Phone, FileSignature, AlertTriangle, CheckCircle2, FileText } from 'lucide-react';
import Header from '../components/Header';
import DatePickerField from '../components/DatePickerField';
import EvidenceUploads from '../components/EvidenceUploads';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const C = '#42CDD7'; // i3 teal

const STATES = ['NSW', 'VIC', 'QLD', 'WA', 'SA', 'TAS', 'ACT', 'NT'];

export default function PracticeSettingsPage() {
  const { user } = useAuth();
  const isPM = user?.role === 'admin' || user?.role === 'superuser';
  const [d, setD] = useState({});
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    axios.get(`${API}/practice-settings`, { withCredentials: true })
      .then(r => setD(r.data || {}))
      .catch(() => {});
  }, []);

  const set = (k, v) => setD(s => ({ ...s, [k]: v }));

  const save = async () => {
    setSaving(true); setMsg(null);
    try {
      const r = await axios.put(`${API}/practice-settings`, d, { withCredentials: true });
      setD(r.data);
      setMsg({ type: 'success', text: 'Practice settings saved.' });
    } catch (e) {
      setMsg({ type: 'error', text: e.response?.data?.detail || 'Save failed.' });
    }
    setSaving(false);
  };

  const seedI3Defaults = () => {
    if (!window.confirm('Pre-fill every empty field with i3 Radiology\'s known identifiers? Existing values are preserved.')) return;
    const I3 = {
      practice_name: 'Infinity Imaging and Interventions Pty Ltd',
      trading_name: 'i3 Radiology',
      abn: '44672236805',
      lspn: '13313',
      hpi_o: '8003625833443371',
      address_line_1: '176 Princes Highway',
      suburb: 'Dapto',
      state: 'NSW',
      postcode: '2530',
      country: 'Australia',
      phone: '0242015200',
      fax: '0242015299',
    };
    setD(s => {
      const next = { ...s };
      Object.entries(I3).forEach(([k, v]) => {
        if (!String(s?.[k] || '').trim()) next[k] = v;
      });
      return next;
    });
    setMsg({ type: 'success', text: 'Defaults applied — review the form, then click Save changes to persist.' });
  };

  return (
    <div className="min-h-screen bg-background" data-testid="practice-settings-page">
      <Header />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full mb-3" style={{ backgroundColor: `${C}1A`, color: C }}>
              <Building2 className="w-3.5 h-3.5" />
              <span className="text-[10px] font-bold uppercase tracking-wider">Practice settings</span>
            </div>
            <h1 className="font-['Outfit'] text-4xl sm:text-5xl font-bold text-foreground">i3 Radiology · identifiers</h1>
            <p className="mt-3 text-sm sm:text-base text-muted-foreground max-w-2xl">
              The single source of truth for Medicare LSPN, HPI-O, ABN, accreditation numbers and the practice address. Read by consent forms, referral letters, the Friday DIAS digest, and (eventually) the QIP-share page.
            </p>
          </div>
          {isPM && (
            <div className="flex items-center gap-2 self-start">
              {/* Iter 91 — small icon shortcut to the printable letterhead
                  generator. Moved here from the Referrals export menu since
                  the letterhead is a practice-identity artefact (logo +
                  address + map), not a referral export. */}
              <Link to="/letterhead" data-testid="prac-letterhead-btn"
                title="Open the printable A4 letterhead (Stripes / Colour-band styles, with map + QR back page)"
                className="inline-flex items-center justify-center w-9 h-9 rounded-xl border hover:bg-muted/50"
                style={{ borderColor: `${C}40`, color: C }}>
                <FileText className="w-4 h-4" />
              </Link>
              <button onClick={seedI3Defaults} data-testid="prac-seed-i3-btn"
                title="Pre-fill all empty fields with i3's known identifiers (ABN, LSPN, HPI-O, Dapto address, phone/fax). Doesn't overwrite values you've already entered."
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-xl border"
                style={{ borderColor: `${C}40`, color: C }}>
                <Sparkles className="w-4 h-4" /> Seed i3 defaults
              </button>
            </div>
          )}
        </div>

        {msg && (
          <div data-testid="prac-msg" className={`rounded-lg border p-3 text-sm mb-4 flex items-start gap-2 ${msg.type === 'success'
            ? 'bg-green-500/5 border-green-500/30 text-green-600'
            : 'bg-red-500/5 border-red-500/30 text-red-600'}`}>
            {msg.type === 'success' ? <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" /> : <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />} {msg.text}
          </div>
        )}

        {!isPM && (
          <div className="rounded-lg border border-border bg-muted/30 p-3 text-xs text-muted-foreground mb-4">
            View-only. Practice Manager / admin can edit.
          </div>
        )}

        <Section icon={Building2} label="Identity">
          <Row><F label="Practice name" value={d.practice_name} onChange={v => set('practice_name', v)} disabled={!isPM} testid="prac-name" /></Row>
          <Row><F label="Trading name (if different)" value={d.trading_name} onChange={v => set('trading_name', v)} disabled={!isPM} testid="prac-trading" /></Row>
          <Row><F label="ABN" value={d.abn} onChange={v => set('abn', v)} disabled={!isPM} testid="prac-abn" placeholder="e.g. 12 345 678 901" /></Row>
        </Section>

        <Section icon={Receipt} label="Medicare & HealthLink">
          <Row><F label="LSPN — Location Specific Practice Number" value={d.lspn} onChange={v => set('lspn', v)} disabled={!isPM} testid="prac-lspn"
            help="Issued by Services Australia. Required on every Medicare diagnostic-imaging claim." /></Row>
          <Row><F label="HPI-O — Healthcare Provider Identifier (Organisation)" value={d.hpi_o} onChange={v => set('hpi_o', v)} disabled={!isPM} testid="prac-hpi-o"
            help="16-digit number from Services Australia HI Service. Used on every HealthLink eReferral / report sent in or out of the practice." /></Row>
          <Row><F label="HealthLink EDI" value={d.healthlink_edi} onChange={v => set('healthlink_edi', v)} disabled={!isPM} testid="prac-edi"
            help="Inbound secure-messaging address for receiving referrals." /></Row>
        </Section>

        <Section icon={Shield} label="Accreditation">
          <Row>
            <F label="DIAS accreditation #" value={d.dias_accreditation_no} onChange={v => set('dias_accreditation_no', v)} disabled={!isPM} testid="prac-dias-no" />
            <DateF label="DIAS expiry" value={d.dias_accreditation_expiry} onChange={v => set('dias_accreditation_expiry', v)} disabled={!isPM} testid="prac-dias-exp" />
          </Row>
          <Row><F label="NATA accreditation # (if applicable)" value={d.nata_accreditation_no} onChange={v => set('nata_accreditation_no', v)} disabled={!isPM} testid="prac-nata" /></Row>
          <div className="pt-3 mt-2 border-t border-border space-y-3">
            <EvidenceUploads parentType="practice_cert" parentId="practice"
              label="Practice certificates (DIAS, NATA, ABN, insurance)" testidPrefix="prac-evidence" />
            <EvidenceUploads parentType="practice_cert" parentId="rsp"
              label="Radiation Safety Plan (RSP) — DIAS 1.3" testidPrefix="prac-rsp-evidence" />
            <EvidenceUploads parentType="practice_cert" parentId="rml"
              label="Radiation Management Licence (RML) — DIAS 1.3" testidPrefix="prac-rml-evidence" />
          </div>
        </Section>

        <Section icon={MapPin} label="Address">
          <Row><F label="Address line 1" value={d.address_line_1} onChange={v => set('address_line_1', v)} disabled={!isPM} testid="prac-addr1" /></Row>
          <Row><F label="Address line 2" value={d.address_line_2} onChange={v => set('address_line_2', v)} disabled={!isPM} testid="prac-addr2" /></Row>
          <Row>
            <F label="Suburb" value={d.suburb} onChange={v => set('suburb', v)} disabled={!isPM} testid="prac-suburb" />
            <Sel label="State" value={d.state || 'NSW'} onChange={v => set('state', v)} options={STATES} disabled={!isPM} testid="prac-state" />
            <F label="Postcode" value={d.postcode} onChange={v => set('postcode', v)} disabled={!isPM} testid="prac-postcode" />
          </Row>
          {/* Iter 88 — geo coords for the letterhead reverse-side map. */}
          <Row>
            <F label="Latitude (for letterhead map)" value={d.latitude} onChange={v => set('latitude', v)}
               disabled={!isPM} testid="prac-lat"
               help="e.g. -34.4923 — drop a pin in Google Maps, right-click → copy coordinates." />
            <F label="Longitude (for letterhead map)" value={d.longitude} onChange={v => set('longitude', v)}
               disabled={!isPM} testid="prac-lng"
               help="e.g. 150.7867" />
          </Row>
        </Section>

        <Section icon={Phone} label="Contact">
          <Row>
            <F label="Phone" value={d.phone} onChange={v => set('phone', v)} disabled={!isPM} testid="prac-phone" />
            <F label="Fax" value={d.fax} onChange={v => set('fax', v)} disabled={!isPM} testid="prac-fax" />
          </Row>
          <Row>
            <F label="Email" value={d.email} onChange={v => set('email', v)} disabled={!isPM} testid="prac-email" />
            <F label="After-hours phone" value={d.after_hours_phone} onChange={v => set('after_hours_phone', v)} disabled={!isPM} testid="prac-ah" />
          </Row>
        </Section>

        <Section icon={FileSignature} label="Default reporting">
          <Row>
            <F label="Default reporting radiologist" value={d.default_radiologist_name} onChange={v => set('default_radiologist_name', v)} disabled={!isPM} testid="prac-rad" />
            <F label="Provider #" value={d.default_radiologist_provider_no} onChange={v => set('default_radiologist_provider_no', v)} disabled={!isPM} testid="prac-rad-pn" />
          </Row>
          <Row><F label="Report-send-address (RSA inbox)" value={d.rsa_email} onChange={v => set('rsa_email', v)} disabled={!isPM} testid="prac-rsa"
            help="Email/EDI address that report-replies and acknowledgements come back to." /></Row>
        </Section>

        <Section icon={Sparkles} label="Notes">
          <Row><F label="Internal notes" value={d.notes} onChange={v => set('notes', v)} disabled={!isPM} testid="prac-notes" type="textarea" /></Row>
        </Section>

        {isPM && (
          <div className="sticky bottom-4 flex justify-end mt-6">
            <button onClick={save} disabled={saving} data-testid="prac-save-btn"
              className="inline-flex items-center gap-1.5 text-sm font-semibold px-5 py-2.5 rounded-xl text-white shadow-lg disabled:opacity-50"
              style={{ backgroundColor: C }}>
              <Save className="w-4 h-4" /> {saving ? 'Saving…' : 'Save practice settings'}
            </button>
          </div>
        )}

        {d.updated_at && (
          <p className="text-[10px] text-muted-foreground mt-4 text-right">
            Last updated {new Date(d.updated_at).toLocaleString('en-AU')}{d.updated_by && ` by ${d.updated_by}`}
          </p>
        )}
      </div>
    </div>
  );
}

function Section({ icon: Icon, label, children }) {
  return (
    <div className="bg-card border border-border rounded-2xl p-5 mb-4">
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-4 h-4" style={{ color: C }} />
        <h2 className="font-['Outfit'] text-base font-semibold text-foreground">{label}</h2>
      </div>
      <div className="space-y-2.5">{children}</div>
    </div>
  );
}

function Row({ children }) {
  return <div className="grid grid-cols-1 md:grid-cols-2 gap-2">{children}</div>;
}

function F({ label, value, onChange, disabled, testid, placeholder, help, type = 'text' }) {
  const cls = "w-full mt-1 text-sm bg-background border border-border rounded-lg px-3 py-2 disabled:opacity-60";
  return (
    <label className="block">
      <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{label}</span>
      {type === 'textarea'
        ? <textarea value={value || ''} disabled={disabled} onChange={e => onChange(e.target.value)} data-testid={testid} className={cls} rows={3} />
        : <input type="text" value={value ?? ''} disabled={disabled} placeholder={placeholder}
            onChange={e => onChange(e.target.value)} data-testid={testid} className={cls} />}
      {help && <span className="text-[10px] text-muted-foreground italic mt-0.5 block">{help}</span>}
    </label>
  );
}

function Sel({ label, value, onChange, options, disabled, testid }) {
  return (
    <label className="block">
      <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{label}</span>
      <select value={value || ''} onChange={e => onChange(e.target.value)} disabled={disabled} data-testid={testid}
        className="w-full mt-1 text-sm bg-background border border-border rounded-lg px-3 py-2 disabled:opacity-60">
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </label>
  );
}

function DateF({ label, value, onChange, disabled, testid }) {
  return (
    <label className="block">
      <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{label}</span>
      <DatePickerField value={value} onChange={onChange} format="iso" testid={testid} disabled={disabled} />
    </label>
  );
}
